import { useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';
import {
  CheckIcon,
  XMarkIcon,
  EyeIcon,
  SparklesIcon,
  StarIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

interface CatalogListing {
  listingId: string;
  organizationId: string;
  organizationName: string;
  organizationSlug?: string;
  status: 'draft' | 'pending' | 'active' | 'suspended';
  tier: 'basic' | 'featured' | 'spotlight';
  headline: string;
  description: string;
  categories: string[];
  commissionHighlight: string;
  applicationMode: 'auto_approve' | 'manual_review';
  stripeSubscriptionId?: string;
  tierExpiresAt?: string;
  stats: {
    views: number;
    applications: number;
    activeAffiliates: number;
  };
  createdAt: string;
  updatedAt: string;
}

interface CatalogStats {
  total: number;
  pending: number;
  active: number;
  suspended: number;
  byTier: {
    basic: number;
    featured: number;
    spotlight: number;
  };
}

const statusStyles: Record<string, string> = {
  draft: 'bg-cream text-charcoal/60',
  pending: 'badge-warning',
  active: 'badge-success',
  suspended: 'badge-danger',
};

const tierStyles: Record<string, { badge: string; icon: React.ReactNode }> = {
  basic: { badge: 'bg-cream text-charcoal/60', icon: null },
  featured: {
    badge: 'bg-rust/20 text-rust',
    icon: <StarIcon className="w-3 h-3 mr-1" />,
  },
  spotlight: {
    badge: 'bg-sage/20 text-sage',
    icon: <SparklesIcon className="w-3 h-3 mr-1" />,
  },
};

export default function AdminCatalog() {
  const [listings, setListings] = useState<CatalogListing[]>([]);
  const [stats, setStats] = useState<CatalogStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tierFilter, setTierFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [selectedListing, setSelectedListing] = useState<CatalogListing | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const fetchListings = async () => {
    setIsLoading(true);
    try {
      const params: { status?: string; tier?: string; search?: string } = {};
      if (statusFilter) params.status = statusFilter;
      if (tierFilter) params.tier = tierFilter;
      if (search) params.search = search;

      const response = await adminApi.listCatalogListings(params);
      if (response.data.success) {
        setListings(response.data.data.listings);
        setStats(response.data.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch catalog listings:', error);
      toast.error('Failed to load catalog listings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchListings();
  }, [statusFilter, tierFilter]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchListings();
  };

  const handleApprove = async (listingId: string) => {
    try {
      await adminApi.approveCatalogListing(listingId);
      toast.success('Listing approved and now visible in catalog');
      fetchListings();
    } catch {
      toast.error('Failed to approve listing');
    }
  };

  const handleSuspend = async (listingId: string) => {
    const reason = prompt('Reason for suspension (optional):');
    try {
      await adminApi.suspendCatalogListing(listingId, reason || undefined);
      toast.success('Listing suspended');
      fetchListings();
    } catch {
      toast.error('Failed to suspend listing');
    }
  };

  const handleViewDetails = async (listing: CatalogListing) => {
    try {
      const response = await adminApi.getCatalogListing(listing.listingId);
      if (response.data.success) {
        setSelectedListing(response.data.data.listing);
        setShowDetailModal(true);
      }
    } catch {
      toast.error('Failed to load listing details');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-2xl font-bold text-charcoal">Catalog Management</h2>
        <p className="text-charcoal/70 mt-1">Review and moderate affiliate program listings</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <div className="rounded-xl bg-warm-white shadow-sm border border-cream p-4 text-center">
            <p className="font-serif text-2xl font-bold text-charcoal">{stats.total}</p>
            <p className="text-xs text-charcoal/60">Total</p>
          </div>
          <div className="rounded-xl bg-warm-white shadow-sm border border-tan/50 p-4 text-center">
            <p className="font-serif text-2xl font-bold text-tan">{stats.pending}</p>
            <p className="text-xs text-charcoal/60">Pending</p>
          </div>
          <div className="rounded-xl bg-warm-white shadow-sm border border-sage/50 p-4 text-center">
            <p className="font-serif text-2xl font-bold text-sage">{stats.active}</p>
            <p className="text-xs text-charcoal/60">Active</p>
          </div>
          <div className="rounded-xl bg-warm-white shadow-sm border border-rust/50 p-4 text-center">
            <p className="font-serif text-2xl font-bold text-rust">{stats.suspended}</p>
            <p className="text-xs text-charcoal/60">Suspended</p>
          </div>
          <div className="rounded-xl bg-warm-white shadow-sm border border-cream p-4 text-center">
            <p className="font-serif text-2xl font-bold text-charcoal/60">{stats.byTier.basic}</p>
            <p className="text-xs text-charcoal/60">Basic</p>
          </div>
          <div className="rounded-xl bg-warm-white shadow-sm border border-rust/30 p-4 text-center">
            <p className="font-serif text-2xl font-bold text-rust">{stats.byTier.featured}</p>
            <p className="text-xs text-charcoal/60">Featured</p>
          </div>
          <div className="rounded-xl bg-warm-white shadow-sm border border-sage/30 p-4 text-center">
            <p className="font-serif text-2xl font-bold text-sage">{stats.byTier.spotlight}</p>
            <p className="text-xs text-charcoal/60">Spotlight</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input"
            placeholder="Search by organization, headline..."
          />
        </form>
        <div className="flex gap-2 flex-wrap">
          {['', 'pending', 'active', 'suspended'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={clsx(
                'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                statusFilter === status
                  ? 'bg-sage text-white'
                  : 'bg-cream text-charcoal hover:bg-tan/30'
              )}
            >
              {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'All Status'}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap">
          {['', 'basic', 'featured', 'spotlight'].map((tier) => (
            <button
              key={tier}
              onClick={() => setTierFilter(tier)}
              className={clsx(
                'px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                tierFilter === tier
                  ? 'bg-sage text-white'
                  : 'bg-cream text-charcoal hover:bg-tan/30'
              )}
            >
              {tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : 'All Tiers'}
            </button>
          ))}
        </div>
      </div>

      {/* Listings Table */}
      <div className="rounded-xl bg-warm-white shadow-sm border border-cream overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-sage"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-charcoal/60">No catalog listings found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Organization</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Headline</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Tier</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Stats</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-charcoal/70 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-cream">
                {listings.map((listing) => (
                  <tr key={listing.listingId} className="hover:bg-cream/30 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-charcoal">{listing.organizationName}</p>
                        {listing.organizationSlug && (
                          <p className="text-xs text-charcoal/60">/{listing.organizationSlug}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-charcoal/80 max-w-xs truncate">
                        {listing.headline}
                      </p>
                      <p className="text-xs text-rust">{listing.commissionHighlight}</p>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge', statusStyles[listing.status])}>
                        {listing.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={clsx('badge flex items-center', tierStyles[listing.tier].badge)}>
                        {tierStyles[listing.tier].icon}
                        {listing.tier}
                      </span>
                      {listing.stripeSubscriptionId && (
                        <p className="text-xs text-sage mt-1">Paid</p>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-charcoal/60 space-y-0.5">
                        <p>{listing.stats.views} views</p>
                        <p>{listing.stats.applications} apps</p>
                        <p>{listing.stats.activeAffiliates} affiliates</p>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-charcoal/60 text-sm">
                      {format(new Date(listing.createdAt), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleViewDetails(listing)}
                          className="p-2 text-charcoal/60 hover:text-charcoal hover:bg-cream rounded-lg transition-colors"
                          title="View Details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        {listing.status === 'pending' && (
                          <button
                            onClick={() => handleApprove(listing.listingId)}
                            className="p-2 text-sage hover:bg-sage/20 rounded-lg transition-colors"
                            title="Approve"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                        )}
                        {listing.status === 'active' && (
                          <button
                            onClick={() => handleSuspend(listing.listingId)}
                            className="p-2 text-tan hover:bg-tan/20 rounded-lg transition-colors"
                            title="Suspend"
                          >
                            <ExclamationTriangleIcon className="w-4 h-4" />
                          </button>
                        )}
                        {listing.status === 'suspended' && (
                          <button
                            onClick={() => handleApprove(listing.listingId)}
                            className="p-2 text-sage hover:bg-sage/20 rounded-lg transition-colors"
                            title="Reactivate"
                          >
                            <CheckIcon className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedListing && (
        <div className="fixed inset-0 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-warm-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-cream shadow-xl">
            <div className="p-6 border-b border-cream flex justify-between items-start">
              <div>
                <h3 className="font-display text-xl font-bold text-charcoal">{selectedListing.organizationName}</h3>
                <p className="text-charcoal/60 text-sm">{selectedListing.headline}</p>
              </div>
              <button
                onClick={() => setShowDetailModal(false)}
                className="p-2 text-charcoal/60 hover:text-charcoal hover:bg-cream rounded-lg transition-colors"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Status and Tier */}
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-charcoal/50 mb-1">Status</p>
                  <span className={clsx('badge', statusStyles[selectedListing.status])}>
                    {selectedListing.status}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-charcoal/50 mb-1">Tier</p>
                  <span className={clsx('badge flex items-center', tierStyles[selectedListing.tier].badge)}>
                    {tierStyles[selectedListing.tier].icon}
                    {selectedListing.tier}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-charcoal/50 mb-1">Application Mode</p>
                  <span className="badge bg-cream text-charcoal/70">
                    {selectedListing.applicationMode === 'auto_approve' ? 'Auto Approve' : 'Manual Review'}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div>
                <p className="text-xs text-charcoal/50 mb-1">Description</p>
                <p className="text-charcoal/80 text-sm">{selectedListing.description}</p>
              </div>

              {/* Commission */}
              <div>
                <p className="text-xs text-charcoal/50 mb-1">Commission</p>
                <p className="text-rust font-medium">{selectedListing.commissionHighlight}</p>
              </div>

              {/* Categories */}
              <div>
                <p className="text-xs text-charcoal/50 mb-1">Categories</p>
                <div className="flex flex-wrap gap-1">
                  {selectedListing.categories.map((cat) => (
                    <span key={cat} className="badge bg-cream text-charcoal/70">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>

              {/* Stats */}
              <div>
                <p className="text-xs text-charcoal/50 mb-2">Statistics</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-cream/50 rounded-lg p-3 text-center">
                    <p className="font-serif text-xl font-bold text-charcoal">{selectedListing.stats.views}</p>
                    <p className="text-xs text-charcoal/60">Views</p>
                  </div>
                  <div className="bg-cream/50 rounded-lg p-3 text-center">
                    <p className="font-serif text-xl font-bold text-charcoal">{selectedListing.stats.applications}</p>
                    <p className="text-xs text-charcoal/60">Applications</p>
                  </div>
                  <div className="bg-cream/50 rounded-lg p-3 text-center">
                    <p className="font-serif text-xl font-bold text-charcoal">{selectedListing.stats.activeAffiliates}</p>
                    <p className="text-xs text-charcoal/60">Affiliates</p>
                  </div>
                </div>
              </div>

              {/* Subscription Info */}
              {selectedListing.stripeSubscriptionId && (
                <div>
                  <p className="text-xs text-charcoal/50 mb-1">Subscription</p>
                  <p className="text-sage text-sm">
                    Active subscription
                    {selectedListing.tierExpiresAt && (
                      <span className="text-charcoal/60">
                        {' '}(expires {format(new Date(selectedListing.tierExpiresAt), 'MMM d, yyyy')})
                      </span>
                    )}
                  </p>
                </div>
              )}

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-charcoal/50 mb-1">Created</p>
                  <p className="text-charcoal/70">
                    {format(new Date(selectedListing.createdAt), 'PPpp')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-charcoal/50 mb-1">Last Updated</p>
                  <p className="text-charcoal/70">
                    {format(new Date(selectedListing.updatedAt), 'PPpp')}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-cream">
                {selectedListing.status === 'pending' && (
                  <button
                    onClick={() => {
                      handleApprove(selectedListing.listingId);
                      setShowDetailModal(false);
                    }}
                    className="btn btn-primary"
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Approve Listing
                  </button>
                )}
                {selectedListing.status === 'active' && (
                  <button
                    onClick={() => {
                      handleSuspend(selectedListing.listingId);
                      setShowDetailModal(false);
                    }}
                    className="btn btn-danger"
                  >
                    <ExclamationTriangleIcon className="w-4 h-4 mr-2" />
                    Suspend Listing
                  </button>
                )}
                {selectedListing.status === 'suspended' && (
                  <button
                    onClick={() => {
                      handleApprove(selectedListing.listingId);
                      setShowDetailModal(false);
                    }}
                    className="btn btn-primary"
                  >
                    <CheckIcon className="w-4 h-4 mr-2" />
                    Reactivate Listing
                  </button>
                )}
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="btn btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
